import React, { useState, useEffect, useCallback } from "react";
import { Calendar } from "lucide-react";
import { toast } from "react-hot-toast";
import { tournamentChipsService } from "../apis/tournamentChipsService";
import DateRangePicker from "../components/ui/date-range-picker";
import { BalanceSummaryBar } from "../components/cash-box/BalanceSummaryBar";
import { DenominationTable } from "../components/cash-box/DenominationTable";
import { BalanceSection } from "../components/cash-box/BalanceSection";
import { SaveLogsPanel } from "../components/cash-box/SaveLogsPanel";
import { getCstToday, formatCstDate } from "../utils/cstDate";

const EMPTY_CHIPS = { 100: 0, 500: 0, 1000: 0, 5000: 0, 25000: 0, 1000000: 0 };

const formatApiDate = (date) => formatCstDate(date);

const TournamentPokerChips = () => {
  const [startingChips, setStartingChips] = useState({ ...EMPTY_CHIPS });
  const [closingChips, setClosingChips] = useState({ ...EMPTY_CHIPS });
  const [savedBalance, setSavedBalance] = useState(null);
  const [selectedDate, setSelectedDate] = useState({
    start: getCstToday(),
    end: getCstToday(),
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isEditingStartingBalance, setIsEditingStartingBalance] = useState(false);
  const [startingDraftChips, setStartingDraftChips] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveLogs, setSaveLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const formatDateTimeInCST = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      timeZone: "America/Chicago",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }, []);

  const getDateTimeParts = (dateTimeString) => {
    const parts = dateTimeString.split(", ");
    return { date: parts[0], time: parts[1] };
  };

  const resetToDefaults = () => {
    setStartingChips({ ...EMPTY_CHIPS });
    setClosingChips({ ...EMPTY_CHIPS });
    setSavedBalance(null);
    setIsEditingStartingBalance(false);
    setStartingDraftChips(null);
  };

  const fetchSaveLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const formattedDate = formatApiDate(selectedDate.start);
      const response = await tournamentChipsService.getSaveLogsByDate(formattedDate);
      if (response.success && response.data?.data) {
        setSaveLogs(response.data.data);
      } else {
        setSaveLogs([]);
      }
    } catch (error) {
      console.error("Error fetching save logs:", error);
      setSaveLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [selectedDate.start]);

  const fetchBalanceData = useCallback(async () => {
    try {
      setIsLoading(true);
      const formattedDate = formatApiDate(selectedDate.start);
      const response = await tournamentChipsService.getRecordByDate(formattedDate);

      if (response.data && response.data.data) {
        const record = response.data.data;

        const mappedStartingChips = {
          100: record.starting_chips_100 ?? 0,
          500: record.starting_chips_500 ?? 0,
          1000: record.starting_chips_1000 ?? 0,
          5000: record.starting_chips_5000 ?? 0,
          25000: record.starting_chips_25000 ?? 0,
          1000000: record.starting_chips_1000000 ?? 0,
        };
        const mappedClosingChips = {
          100: record.closing_chips_100 ?? 0,
          500: record.closing_chips_500 ?? 0,
          1000: record.closing_chips_1000 ?? 0,
          5000: record.closing_chips_5000 ?? 0,
          25000: record.closing_chips_25000 ?? 0,
          1000000: record.closing_chips_1000000 ?? 0,
        };

        setStartingChips(mappedStartingChips);
        setClosingChips(mappedClosingChips);

        if (record.updated_at) {
          const formattedDateTime = formatDateTimeInCST(record.updated_at);
          const { date, time } = getDateTimeParts(formattedDateTime);
          setSavedBalance({
            date,
            time,
            totals: {
              closingChipsTotal: parseFloat(record.closing_chips_total || 0),
            },
          });
        } else {
          setSavedBalance(null);
        }
      } else {
        resetToDefaults();
      }
    } catch (error) {
      console.error("Error fetching balance data:", error);
      toast.error("Failed to load balance data");
      resetToDefaults();
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate.start, formatDateTimeInCST]);

  useEffect(() => {
    fetchBalanceData();
    fetchSaveLogs();
  }, [selectedDate, fetchBalanceData, fetchSaveLogs]);

  const calculateTotal = (items) =>
    Object.entries(items).reduce((total, [denomination, quantity]) => {
      return total + parseInt(denomination, 10) * parseInt(quantity || 0, 10);
    }, 0);

  const normalizeQuantities = (items) =>
    Object.fromEntries(
      Object.entries(items).map(([denomination, quantity]) => [
        denomination,
        quantity === "" || quantity == null ? 0 : parseInt(quantity, 10) || 0,
      ])
    );

  const displayStartingChips = isEditingStartingBalance
    ? startingDraftChips || startingChips
    : startingChips;

  const startingChipsTotal = calculateTotal(displayStartingChips);
  const closingChipsTotal = calculateTotal(closingChips);
  const difference = closingChipsTotal - startingChipsTotal;

  const handleQtyChange = (setter) => (denomination, value) => {
    if (value === "" || /^\d+$/.test(value)) {
      setter((prev) => ({
        ...(prev || {}),
        [denomination]: value === "" ? "" : parseInt(value, 10),
      }));
    }
  };

  const beginEditStarting = () => {
    setStartingDraftChips({ ...startingChips });
    setIsEditingStartingBalance(true);
  };

  const cancelEditStarting = () => {
    setStartingDraftChips(null);
    setIsEditingStartingBalance(false);
  };

  const saveStartingBalance = async () => {
    try {
      setIsLoading(true);
      const formattedDate = formatApiDate(selectedDate.start);
      const response = await tournamentChipsService.updateStartingBalance(
        formattedDate,
        {},
        normalizeQuantities(displayStartingChips)
      );

      if (!response.success) {
        toast.error(response.error || "Failed to update starting balance");
        return;
      }

      toast.success("Starting balance updated successfully!");
      setIsEditingStartingBalance(false);
      setStartingDraftChips(null);
      await fetchBalanceData();
      await fetchSaveLogs();
    } catch (error) {
      console.error("Error updating starting balance:", error);
      toast.error("Failed to update starting balance");
    } finally {
      setIsLoading(false);
    }
  };

  const saveClosingBalance = async () => {
    try {
      setIsLoading(true);
      const formattedDate = formatApiDate(selectedDate.start);
      const nextStartingChips = normalizeQuantities(closingChips);

      const response = await tournamentChipsService.updateClosingBalance(
        formattedDate,
        {},
        nextStartingChips
      );

      if (!response.success) {
        toast.error(response.error || "Failed to save closing balance");
        return;
      }

      // Difference is absorbed into Starting: next period begins from this closing count
      const startingResponse = await tournamentChipsService.updateStartingBalance(
        formattedDate,
        {},
        nextStartingChips
      );

      if (!startingResponse.success) {
        toast.error(
          startingResponse.error ||
            "Closing saved, but failed to roll into starting balance"
        );
        return;
      }

      setStartingChips(nextStartingChips);
      setClosingChips(nextStartingChips);
      setIsEditingStartingBalance(false);
      setStartingDraftChips(null);

      const formattedDateTime = formatDateTimeInCST(new Date());
      const { date, time } = getDateTimeParts(formattedDateTime);
      setSavedBalance({
        date,
        time,
        totals: {
          closingChipsTotal: calculateTotal(nextStartingChips),
        },
      });

      toast.success(
        "Closing saved — starting balance now matches closing for next count"
      );
      await fetchBalanceData();
      await fetchSaveLogs();
    } catch (error) {
      console.error("Error saving closing balance:", error);
      toast.error("Failed to save closing balance");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateRangeApply = (start, end) => {
    setSelectedDate({ start, end: end || start });
    setIsDatePickerOpen(false);
  };

  const lastSavedLabel = savedBalance
    ? `Last saved ${savedBalance.date} at ${savedBalance.time}`
    : null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 rounded-lg border border-gray-200 bg-white px-5 py-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Tournament Poker Chips
              </h1>
            </div>
            <button
              type="button"
              onClick={() => setIsDatePickerOpen(true)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:border-blue-500 hover:text-blue-600"
            >
              <Calendar className="h-4 w-4 text-gray-500" />
              {selectedDate.start.toLocaleDateString()}
              {selectedDate.end &&
                selectedDate.end.toLocaleDateString() !==
                  selectedDate.start.toLocaleDateString() &&
                ` – ${selectedDate.end.toLocaleDateString()}`}
            </button>
          </div>

          <DateRangePicker
            isOpen={isDatePickerOpen}
            onClose={() => setIsDatePickerOpen(false)}
            onApply={handleDateRangeApply}
            initialStartDate={selectedDate.start}
            initialEndDate={selectedDate.end}
          />
        </div>

        <BalanceSummaryBar
          startingTotal={startingChipsTotal}
          closingTotal={closingChipsTotal}
          difference={difference}
          onSaveClosing={saveClosingBalance}
          isSaving={isLoading}
          lastSavedLabel={lastSavedLabel}
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <BalanceSection
            title="Starting Balance"
            step={1}
            subtitle="Locked by default. Unlock only if you need to correct the opening chip count."
            tone="starting"
            actions={
              isEditingStartingBalance ? (
                <>
                  <button
                    type="button"
                    onClick={cancelEditStarting}
                    disabled={isLoading}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveStartingBalance}
                    disabled={isLoading}
                    className="rounded-md bg-yellow-400 px-3 py-1.5 text-sm font-semibold text-black hover:bg-yellow-500 disabled:opacity-60"
                  >
                    Save Starting
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={beginEditStarting}
                  disabled={isLoading}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:border-blue-500 hover:text-blue-600"
                >
                  Edit Starting
                </button>
              )
            }
            footer={
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-emerald-800">
                  Starting chips total
                </span>
                <span className="text-lg font-bold text-gray-900">
                  ${startingChipsTotal.toLocaleString()}
                </span>
              </div>
            }
          >
            <DenominationTable
              title="Chips"
              type="chips"
              mode="starting"
              items={displayStartingChips}
              editable={isEditingStartingBalance}
              onQuantityChange={handleQtyChange(setStartingDraftChips)}
            />
          </BalanceSection>

          <BalanceSection
            title="Closing Balance"
            step={2}
            subtitle="Always editable. Type this interval’s chip count — Change shows Δ vs Starting."
            tone="closing"
            footer={
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-blue-800">
                  Closing chips total
                </span>
                <span className="text-lg font-bold text-gray-900">
                  ${closingChipsTotal.toLocaleString()}
                </span>
              </div>
            }
          >
            <DenominationTable
              title="Chips"
              type="chips"
              mode="closing"
              items={closingChips}
              startingItems={displayStartingChips}
              editable
              onQuantityChange={handleQtyChange(setClosingChips)}
            />
          </BalanceSection>
        </div>

        <SaveLogsPanel
          title="Day Save Logs"
          logs={saveLogs}
          isLoading={logsLoading}
        />
      </div>
    </div>
  );
};

export default TournamentPokerChips;
